/* app/views/base.js
Base Views for the Bento Box App:
All views in the app should inherit from BaseView, which adds a handful
of convenience methods for controlling view display and state.
ContainerView is for views that contain child subviews.
*/

import Backbone from 'backbone';
import EventChannel from '../events.js';
import $ from 'jquery';


/* BaseView -- All views in the app should inherit from this. It allows
 * a controller to activate (show) and deactivate (hide) the view, and
 * provides a 'reset' method for resetting state. */
const BaseView = Backbone.View.extend({
  /* set 'renderEventLabel' in your subclass if you want an event
   * triggered when rendering is complete */
  renderEventLabel: null,

  reset: function(view) {
    /* Subclasses should implement this method to reset state. */
    return true;
  },

  activate: function(vars, view) {
    /* Method for showing the view once it's been rendered. */
    (view || this).render(vars).$el.show();
  },

  deactivate: function(view) {
    /* Method for deactivating the view. */
    view = view || this;
    view.$el.hide();
    view.reset();
  },

  isNeeded: function(vars, view) {
    /* Subclasses may implement this method to use the given vars to
     * determine whether this view needs to be used or activated in a
     * given context. */
    return true;
  },

  triggerRenderEvent: function(vars, view) {
    /* Method that triggers the rendering event notification. */
    view = view || this;
    EventChannel.trigger(view.renderEventLabel, vars);
  },

  prepVars: function(vars) {
    /* Subclasses should implement this method to compile whatever data
     * is needed for 'vars' that are fed to the view template during
     * rendering. */
    return vars;
  },

  prepContainer: function(vars, container, view)  {
    /* Subclasses may implement this method if any prep steps need to 
     * be done on the view container just before it is appended to $el,
     * during rendering. */
    return container;
  },

  render: function(vars, view) {
    /* Default generalized implementation of 'render' -- subclasses may
     * want to override this, but it is designed such that they can use
     * prepVars and prepContainer, as needed, instead. */
    view = view || this;
    vars = view.prepVars(vars);
    let container = $(view.template(vars));
    view.$el.empty().append(view.prepContainer(vars, container));
    if (view.renderEventLabel) {
      view.triggerRenderEvent(vars);
    }
    return this;
  },
});


/* ContainerView -- Views that contain other views as subviews or
 * children should subclass this. */
const ContainerView = BaseView.extend({
  subViews: [],

  reset: function(view) {
    view = view || this;
    view.subViews.forEach((subView) => subView.reset());
    view.clearSubViews();
  },

  clearSubViews: function(view) {
    view = view || this;
    view.subViews.forEach((subView) => subView.remove());
    view.subViews = [];
  },

  initSubViews: function(vars) {
    /* Subclasses must implement this. It should initialize applicable
     * subView objects based on provided vars and return them as an
     * array. */
    return [];
  },

  getSubViewHook: function(vars, subView, container) {
    /* Subclasses must implement this method to return the element to
     * hook a given subview onto. You may, for example, create a new
     * element, attach it to the container, and then return that
     * element. */
    return container;
  },

  prepContainer: function(vars, container, view) {
    /* Subclasses may override this, but it's designed such that they
     * can use initSubViews and getSubViewHook, as needed, instead. */
    view = view || this;
    view.clearSubViews();
    view.subViews = view.initSubViews(vars);
    view.subViews.forEach((subView) => {
      subView.setElement(view.getSubViewHook(vars, subView, container)).render(vars);
    });
    return container;
  },
});


export {
  BaseView,
  ContainerView,
};
